/**
 * Técnicos e ordens de serviço (spec 016). A OS nasce do ticket — a ficha do
 * veículo que o SGR escreve na descrição preenche o rascunho (ver sgr.ts) — e
 * vai para um técnico escolhido pela disponibilidade do dia.
 */
import { z } from 'zod';
import { Dateish } from './common';
import type { DeskNature } from './desks';
import { capitalize, type Terms } from './profiles';
import { normalizeForCompare } from './sgr';

const ISODate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');

/** Texto opcional de formulário: '' vira null (limpar o campo limpa de verdade). */
const OptText = (max = 500) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullish();

// ── Enums ───────────────────────────────────────────────────────────────────
export const SERVICE_ORDER_TYPES = ['installation', 'maintenance', 'removal', 'other'] as const;
export const ServiceOrderTypeSchema = z.enum(SERVICE_ORDER_TYPES);
export type ServiceOrderType = z.infer<typeof ServiceOrderTypeSchema>;

export const SERVICE_ORDER_TYPE_LABELS: Record<ServiceOrderType, string> = {
  installation: 'Instalação',
  maintenance: 'Manutenção',
  removal: 'Retirada',
  other: 'Outro',
};

export const SERVICE_ORDER_STATUSES = [
  'draft',
  'scheduled',
  'done',
  'unproductive',
  'canceled',
] as const;
export const ServiceOrderStatusSchema = z.enum(SERVICE_ORDER_STATUSES);
export type ServiceOrderStatus = z.infer<typeof ServiceOrderStatusSchema>;

export const SERVICE_ORDER_STATUS_LABELS: Record<ServiceOrderStatus, string> = {
  draft: 'Rascunho',
  scheduled: 'Agendada',
  done: 'Concluída',
  unproductive: 'Improdutiva',
  canceled: 'Cancelada',
};

export const SERVICE_ORDER_PERIODS = ['morning', 'afternoon', 'full_day'] as const;
export const ServiceOrderPeriodSchema = z.enum(SERVICE_ORDER_PERIODS);
export type ServiceOrderPeriod = z.infer<typeof ServiceOrderPeriodSchema>;

export const SERVICE_ORDER_PERIOD_LABELS: Record<ServiceOrderPeriod, string> = {
  morning: 'Manhã',
  afternoon: 'Tarde',
  full_day: 'Dia todo',
};

/**
 * Status que OCUPAM a capacidade do técnico no dia. Concluída conta — o
 * serviço aconteceu naquele dia; improdutiva e cancelada liberam a vaga
 * (US4.2); rascunho ainda não é compromisso.
 */
export const SERVICE_ORDER_LOAD_STATUSES: readonly ServiceOrderStatus[] = ['scheduled', 'done'];

/**
 * Transições permitidas. Estado final não volta: uma OS improdutiva gera OUTRA
 * OS (retorno), que é como a equipe já trabalha — e a primeira fica como
 * registro da visita perdida.
 */
export const SERVICE_ORDER_TRANSITIONS: Record<ServiceOrderStatus, readonly ServiceOrderStatus[]> = {
  draft: ['scheduled', 'canceled'],
  scheduled: ['done', 'unproductive', 'canceled'],
  done: [],
  unproductive: [],
  canceled: [],
};

/** Status que exigem observação ao entrar (motivo). */
export const SERVICE_ORDER_NOTE_REQUIRED: readonly ServiceOrderStatus[] = ['unproductive', 'canceled'];

/** OS ainda editável (campos, técnico, data). */
export function isServiceOrderEditable(status: ServiceOrderStatus): boolean {
  return status === 'draft' || status === 'scheduled';
}

// ── Técnicos ────────────────────────────────────────────────────────────────
export const TECHNICIAN_DEFAULT_DAILY_CAPACITY = 4;

export const TechnicianSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  phone: z.string().nullable(),
  document: z.string().nullable(),
  cities: z.array(z.string()),
  dailyCapacity: z.number().int(),
  active: z.boolean(),
  notes: z.string().nullable(),
  /**
   * Código do técnico na origem sincronizada (a lista de técnicos da PoolTrack,
   * lida toda noite). null = cadastrado à mão no ERP — a sincronização nunca o
   * toca. Com código, nome, documento e cidades são da origem: a edição local
   * deles é recusada (409 technician_synced_field), porque a rodada seguinte
   * os reescreveria em silêncio.
   */
  sgrId: z.string().nullable(),
  /** situação como a origem a escreve (ATIVO, CANCELADO, NÃO EMITE NFE…) */
  sgrStatus: z.string().nullable(),
  region: z.string().nullable(),
  supplier: z.string().nullable(),
  /** última rodada em que a origem trouxe este técnico */
  sgrSyncedAt: Dateish.nullable(),
  createdAt: Dateish,
  updatedAt: Dateish,
});
export type Technician = z.infer<typeof TechnicianSchema>;

const CitiesSchema = z
  .array(z.string().trim().max(120))
  .max(100)
  // vazio some e duplicata (sem acento/caixa) também — "BH" digitado duas vezes
  // não vira dois atendimentos
  .transform((list) => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const city of list) {
      const key = normalizeForCompare(city);
      if (key === '' || seen.has(key)) continue;
      seen.add(key);
      out.push(city);
    }
    return out;
  });

export const TechnicianBodySchema = z.object({
  name: z.string().trim().min(1).max(200),
  phone: OptText(60),
  document: OptText(60),
  cities: CitiesSchema.default([]),
  dailyCapacity: z.number().int().min(1).max(50).default(TECHNICIAN_DEFAULT_DAILY_CAPACITY),
  active: z.boolean().default(true),
  notes: OptText(2000),
});
export type TechnicianBody = z.infer<typeof TechnicianBodySchema>;

export const TechnicianPatchBodySchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  phone: OptText(60),
  document: OptText(60),
  cities: CitiesSchema.optional(),
  dailyCapacity: z.number().int().min(1).max(50).optional(),
  active: z.boolean().optional(),
  notes: OptText(2000),
});
export type TechnicianPatchBody = z.infer<typeof TechnicianPatchBodySchema>;

export const TechnicianAbsenceSchema = z.object({
  id: z.number().int(),
  technicianId: z.number().int(),
  startDate: ISODate,
  endDate: ISODate,
  reason: z.string().nullable(),
});
export type TechnicianAbsence = z.infer<typeof TechnicianAbsenceSchema>;

export const TechnicianAbsenceBodySchema = z
  .object({
    startDate: ISODate,
    endDate: ISODate,
    reason: OptText(500),
  })
  .refine((b) => b.endDate >= b.startDate, {
    message: 'fim antes do início',
    path: ['endDate'],
  });
export type TechnicianAbsenceBody = z.infer<typeof TechnicianAbsenceBodySchema>;

export const TechnicianListQuerySchema = z.object({
  /** padrão: todos (ativos e inativos) — a tela de cadastro mostra os dois */
  active: z.enum(['true', 'false']).optional(),
  /**
   * Busca sem acento e sem caixa em nome, cidades e região. O seletor do ticket
   * pergunta ao servidor em vez de baixar a lista inteira: a origem sincronizada
   * tem ~1.800 técnicos.
   */
  search: z.string().trim().max(120).optional(),
  /** teto da resposta (o seletor pede poucos); ausente = todos */
  limit: z.coerce.number().int().min(1).max(200).optional(),
});
export type TechnicianListQuery = z.infer<typeof TechnicianListQuerySchema>;

// ── Sincronização de técnicos com a origem ──────────────────────────────────
/**
 * A lista de técnicos vem da origem da PoolTrack (somente leitura): uma rodada
 * agendada por dia e o "sincronizar agora" do admin. O nome da origem chega da
 * API (`sourceName`), não daqui — palavra de sistema de um cliente não é texto
 * do SPA (guarda de vocabulário, spec 017).
 */
export const TECHNICIAN_SYNC_TRIGGERS = ['schedule', 'manual'] as const;
export type TechnicianSyncTrigger = (typeof TECHNICIAN_SYNC_TRIGGERS)[number];

export const TECHNICIAN_SYNC_RUN_STATUSES = ['running', 'ok', 'error'] as const;
export type TechnicianSyncRunStatus = (typeof TECHNICIAN_SYNC_RUN_STATUSES)[number];

/**
 * Por que uma rodada falhou. Toda falha é FECHADA: nada do cadastro muda, e o
 * motivo fica registrado para o admin ler.
 */
export const TECHNICIAN_SYNC_ERROR_KINDS = [
  'login_failed',
  'session_failed',
  'session_expired',
  'http_error',
  'timeout',
  'network_error',
  'invalid_response',
  'incomplete',
  'pagination_inconsistent',
  'format_changed',
  'empty_list',
  'write_failed',
  'crashed',
  'stale',
] as const;
export type TechnicianSyncErrorKind = (typeof TECHNICIAN_SYNC_ERROR_KINDS)[number];

export const TECHNICIAN_SYNC_ERROR_LABELS: Record<TechnicianSyncErrorKind, string> = {
  login_failed: 'A origem recusou o login (código do cliente, usuário ou senha).',
  session_failed: 'O login passou, mas a sessão não abriu.',
  session_expired: 'A origem encerrou a sessão no meio da leitura.',
  http_error: 'A origem respondeu com erro.',
  timeout: 'A origem demorou demais para responder.',
  network_error: 'Não foi possível falar com a origem.',
  invalid_response: 'A origem respondeu algo que não é a lista de técnicos.',
  incomplete: 'A lista veio com menos técnicos do que a origem informa.',
  pagination_inconsistent: 'As páginas da lista repetiram técnicos.',
  format_changed: 'O formato da lista mudou: muitas linhas não puderam ser lidas.',
  empty_list: 'A origem devolveu a lista vazia.',
  write_failed: 'A lista foi lida, mas a gravação no ERP falhou.',
  crashed: 'A rodada parou por um erro inesperado.',
  stale: 'A rodada anterior não terminou (processo reiniciado).',
};

export const TechnicianSyncCountsSchema = z.object({
  /** linhas válidas que a origem devolveu */
  read: z.number().int(),
  created: z.number().int(),
  updated: z.number().int(),
  /** passaram de ativo a inativo porque a origem os marcou cancelados */
  deactivated: z.number().int(),
  unchanged: z.number().int(),
  /** linhas da origem que não puderam ser lidas (sem código numérico) */
  skipped: z.number().int(),
});
export type TechnicianSyncCounts = z.infer<typeof TechnicianSyncCountsSchema>;

export const TechnicianSyncRunSchema = z.object({
  id: z.number().int(),
  trigger: z.enum(TECHNICIAN_SYNC_TRIGGERS),
  triggeredBy: z.object({ id: z.number().int(), name: z.string() }).nullable(),
  status: z.enum(TECHNICIAN_SYNC_RUN_STATUSES),
  errorKind: z.enum(TECHNICIAN_SYNC_ERROR_KINDS).nullable(),
  counts: TechnicianSyncCountsSchema.nullable(),
  startedAt: Dateish,
  finishedAt: Dateish.nullable(),
});
export type TechnicianSyncRun = z.infer<typeof TechnicianSyncRunSchema>;

/**
 * GET /technicians/sync (admin). `available: false` = este tenant não é o da
 * integração (a tela não mostra o bloco). Com `available`, `enabled: false`
 * diz o que falta no ambiente — só NOMES de variável, nunca valor.
 */
export const TechnicianSyncStatusSchema = z.union([
  z.object({ available: z.literal(false) }),
  z.object({
    available: z.literal(true),
    enabled: z.boolean(),
    missingConfig: z.array(z.string()),
    sourceName: z.string(),
    /** "todo dia às 03:00 (horário de Brasília)" */
    scheduleLabel: z.string(),
    lastRun: TechnicianSyncRunSchema.nullable(),
    lastSuccessAt: Dateish.nullable(),
  }),
]);
export type TechnicianSyncStatus = z.infer<typeof TechnicianSyncStatusSchema>;

export const TechnicianAvailabilityQuerySchema = z.object({
  date: ISODate,
  city: z.string().trim().max(120).optional(),
});
export type TechnicianAvailabilityQuery = z.infer<typeof TechnicianAvailabilityQuerySchema>;

export const TechnicianAvailabilitySchema = z.object({
  technician: TechnicianSchema,
  /** null quando a consulta não informou cidade */
  coversCity: z.boolean().nullable(),
  /** OS que ocupam a capacidade na data (SERVICE_ORDER_LOAD_STATUSES) */
  scheduledCount: z.number().int(),
  full: z.boolean(),
  absent: z.boolean(),
  /** motivo da ausência na data, quando houver */
  absenceReason: z.string().nullable(),
});
export type TechnicianAvailability = z.infer<typeof TechnicianAvailabilitySchema>;

// ── Ordens de serviço ───────────────────────────────────────────────────────
/** Os campos que o atendente preenche/revisa — rascunho, criação e edição. */
const serviceOrderFieldsShape = {
  type: ServiceOrderTypeSchema,
  technicianId: z.number().int().positive().nullish(),
  scheduledDate: ISODate.nullish(),
  scheduledPeriod: ServiceOrderPeriodSchema.nullish(),
  contactName: OptText(200),
  /** telefones separados por vírgula, na ordem da ficha */
  contactPhones: OptText(200),
  vehiclePlate: OptText(20),
  vehicleBrand: OptText(80),
  vehicleModel: OptText(120),
  vehicleColor: OptText(40),
  vehicleChassis: OptText(40),
  vehicleYear: OptText(20),
  addressStreet: OptText(200),
  addressNumber: OptText(40),
  addressComplement: OptText(200),
  addressNeighborhood: OptText(120),
  addressCity: OptText(120),
  addressState: OptText(40),
  addressZip: OptText(20),
  instructions: OptText(8000),
};

export const ServiceOrderCreateBodySchema = z.object({
  ...serviceOrderFieldsShape,
  /** salvar já agendada exige técnico + data (mesma regra da transição) */
  status: z.enum(['draft', 'scheduled']).default('draft'),
});
export type ServiceOrderCreateBody = z.infer<typeof ServiceOrderCreateBodySchema>;

export const ServiceOrderPatchBodySchema = z.object({
  ...serviceOrderFieldsShape,
  type: ServiceOrderTypeSchema.optional(),
});
export type ServiceOrderPatchBody = z.infer<typeof ServiceOrderPatchBodySchema>;

export const ServiceOrderStatusBodySchema = z.object({
  status: z.enum(['scheduled', 'done', 'unproductive', 'canceled']),
  note: OptText(2000),
});
export type ServiceOrderStatusBody = z.infer<typeof ServiceOrderStatusBodySchema>;

const nullableFields = {
  contactName: z.string().nullable(),
  contactPhones: z.string().nullable(),
  vehiclePlate: z.string().nullable(),
  vehicleBrand: z.string().nullable(),
  vehicleModel: z.string().nullable(),
  vehicleColor: z.string().nullable(),
  vehicleChassis: z.string().nullable(),
  vehicleYear: z.string().nullable(),
  addressStreet: z.string().nullable(),
  addressNumber: z.string().nullable(),
  addressComplement: z.string().nullable(),
  addressNeighborhood: z.string().nullable(),
  addressCity: z.string().nullable(),
  addressState: z.string().nullable(),
  addressZip: z.string().nullable(),
  instructions: z.string().nullable(),
};

/** GET /tickets/:n/service-orders/draft — nada é gravado. */
export const ServiceOrderDraftSchema = z.object({
  ticketNumber: z.number().int(),
  type: ServiceOrderTypeSchema,
  /** a descrição trazia a ficha do SGR (senão a OS abre quase vazia, de propósito) */
  sgrRecognized: z.boolean(),
  deskName: z.string(),
  ...nullableFields,
});
export type ServiceOrderDraft = z.infer<typeof ServiceOrderDraftSchema>;

export const ServiceOrderSchema = z.object({
  id: z.number().int(),
  number: z.number().int(),
  ticketNumber: z.number().int(),
  ticketTitle: z.string(),
  type: ServiceOrderTypeSchema,
  status: ServiceOrderStatusSchema,
  technician: z
    .object({ id: z.number().int(), name: z.string(), phone: z.string().nullable() })
    .nullable(),
  scheduledDate: ISODate.nullable(),
  scheduledPeriod: ServiceOrderPeriodSchema.nullable(),
  ...nullableFields,
  resultNote: z.string().nullable(),
  createdBy: z.object({ id: z.number().int(), name: z.string() }).nullable(),
  createdAt: Dateish,
  updatedAt: Dateish,
  doneAt: Dateish.nullable(),
});
export type ServiceOrder = z.infer<typeof ServiceOrderSchema>;

export const ServiceOrderIdParamSchema = z.object({
  ticketNumber: z.coerce.number().int().positive().max(2_147_483_647),
  id: z.coerce.number().int().positive().max(2_147_483_647),
});

export const SERVICE_ORDER_ERROR_LABELS: Record<string, string> = {
  technician_required: 'Escolha o técnico para agendar.',
  scheduled_date_required: 'Escolha a data para agendar.',
  technician_absent: 'O técnico está ausente nessa data.',
  technician_inactive: 'O técnico está desativado.',
  technician_not_found: 'Técnico não encontrado.',
  technician_synced_field:
    'Nome, documento e cidades deste técnico vêm da sincronização e não se editam aqui.',
  technician_sync_running: 'Já há uma sincronização de técnicos em andamento.',
  technician_sync_disabled: 'A sincronização de técnicos está desligada neste ambiente.',
  note_required: 'Informe o motivo.',
  invalid_transition: 'Essa mudança de status não é permitida.',
  service_order_not_editable: 'A OS já foi encerrada e não pode ser editada.',
};

// ── Regras puras ────────────────────────────────────────────────────────────
/**
 * Tipo da OS pela NATUREZA da mesa do ticket (spec 017, FR-003). Até a 017 o
 * tipo era adivinhado pelo nome da mesa a cada OS (INSTAL/MANUTEN/RETIR); a
 * heurística virou a sugestão da natureza (suggestDeskNature, ./desks) e o que
 * vale agora é o que está gravado na mesa. Suporte, outro e interna → "Outro".
 */
export function serviceOrderTypeFromDeskNature(nature: DeskNature): ServiceOrderType {
  switch (nature) {
    case 'installation':
    case 'maintenance':
    case 'removal':
      return nature;
    default:
      return 'other';
  }
}

/** A cidade da OS está na lista do técnico? Sem acento, sem caixa. */
export function technicianCoversCity(cities: readonly string[], city: string): boolean {
  const wanted = normalizeForCompare(city);
  return cities.some((c) => normalizeForCompare(c) === wanted);
}

const brDate = (iso: string): string => {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

type ServiceOrderTextInput = Pick<
  ServiceOrder,
  | 'number'
  | 'ticketNumber'
  | 'type'
  | 'scheduledDate'
  | 'scheduledPeriod'
  | 'technician'
  | keyof typeof nullableFields
>;

/**
 * Texto da OS para colar no WhatsApp do técnico (US4.1). Linhas sem valor são
 * omitidas; `*negrito*` é a marcação do próprio WhatsApp. Os rótulos do
 * recurso ("Veículo", "Placa") vêm do perfil do tenant (spec 017).
 */
export function renderServiceOrderText(
  os: ServiceOrderTextInput,
  terms: Pick<Terms, 'resource' | 'resourceId'>
): string {
  const lines: string[] = [];
  const push = (label: string, value: string | null | undefined) => {
    if (value) lines.push(`${label}: ${value}`);
  };

  lines.push(`*OS nº ${os.number} — ${SERVICE_ORDER_TYPE_LABELS[os.type]}*`);
  lines.push(`Ticket #${os.ticketNumber}`);
  if (os.scheduledDate) {
    const period = os.scheduledPeriod ? ` — ${SERVICE_ORDER_PERIOD_LABELS[os.scheduledPeriod]}` : '';
    lines.push(`Data: ${brDate(os.scheduledDate)}${period}`);
  }
  push('Técnico', os.technician?.name);

  const vehicle: string[] = [];
  const brandModel = [os.vehicleBrand, os.vehicleModel].filter(Boolean).join(' ');
  if (os.vehiclePlate) vehicle.push(`${capitalize(terms.resourceId)}: ${os.vehiclePlate}`);
  if (brandModel) vehicle.push(`Marca/modelo: ${brandModel}`);
  const colorYear = [
    os.vehicleColor ? `Cor: ${os.vehicleColor}` : null,
    os.vehicleYear ? `Ano: ${os.vehicleYear}` : null,
  ].filter(Boolean);
  if (colorYear.length) vehicle.push(colorYear.join(' · '));
  if (os.vehicleChassis) vehicle.push(`Chassi: ${os.vehicleChassis}`);
  if (vehicle.length) lines.push('', `*${capitalize(terms.resource)}*`, ...vehicle);

  const address: string[] = [];
  const streetLine = [
    [os.addressStreet, os.addressNumber].filter(Boolean).join(', '),
    os.addressComplement,
  ]
    .filter(Boolean)
    .join(' — ');
  if (streetLine) address.push(streetLine);
  if (os.addressNeighborhood) address.push(`Bairro: ${os.addressNeighborhood}`);
  const cityLine = [os.addressCity, os.addressState].filter(Boolean).join('/');
  const cityZip = [cityLine, os.addressZip ? `CEP ${os.addressZip}` : null].filter(Boolean).join(' — ');
  if (cityZip) address.push(cityZip);
  if (address.length) lines.push('', '*Endereço*', ...address);

  const contact = [os.contactName, os.contactPhones].filter(Boolean).join(' — ');
  if (contact) lines.push('', '*Contato*', contact);

  if (os.instructions) lines.push('', '*Instruções*', os.instructions);
  return lines.join('\n');
}
